import { Button, CloseButton, SaveButton, SettingsButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import {
  ISlidingDateRangeTypeOption,
  getSlidingRange,
} from '../../../data/providers/settings/dates/slidingDateRangeProvider';
import {
  IRange,
  IScoreboardSettings,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
} from '../../../data/types';
import { setScoreboardSettings } from '../../../data/writers/settings/scoreboardSettings';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlLabel } from '../../shared/form/ControlLabel';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { SingleMetricSelect } from '../../shared/form/selects/SingleMetricSelect';
import { SlidingDateRangeSelect } from '../../shared/form/selects/SlidingDateRangeSelect';
import { TagSelect } from '../../shared/form/selects/TagsSelect';
import { TestTypeSelect } from '../../shared/form/selects/TestTypeSelect';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';

interface IPopupProps {
  onClose: () => void;
}

const Popup = (props: IProps & IPopupProps): React.ReactElement => {
  const [currentSettings, setCurrentSettings] =
    React.useState<IScoreboardSettings>(props.settings);

  /**
   * @description copys the settings prop to local state for modification
   */
  React.useEffect((): void => {
    setCurrentSettings(props.settings);
  }, [JSON.stringify(props.settings)]);

  /**
   * @description handles the test type change
   * @param testType
   */
  const handleTestTypeChange = (testType: ISelectableTestType): void => {
    setCurrentSettings({ ...currentSettings, testType });
  };

  /**
   * @description handles the tags change
   * @param tags
   */
  const handleTagsChange = (tags: ISelectableTag[]): void => {
    setCurrentSettings({ ...currentSettings, tags });
  };

  /**
   * @description handles metric change
   * @param metric
   */
  const handleMetricChange = (metric: ISelectableMetric): void => {
    setCurrentSettings({
      ...currentSettings,
      metrics: {
        ...currentSettings.metrics,
        [currentSettings.testType.value]: metric,
      },
    });
  };

  /**
   * @description handles date range change
   * @param dateRangeType
   * @param dateRange
   */
  const handleDateChange = (
    dateRangeType: ISlidingDateRangeTypeOption,
    dateRange: IRange
  ): void => {
    const newRange =
      dateRangeType.value === 'custom'
        ? dateRange
        : getSlidingRange(dateRangeType);
    setCurrentSettings({
      ...currentSettings,
      dateRangeType,
      dateRange: newRange,
    });
  };

  /**
   * @description persists the changes to scoreboard settings
   */
  const applyChanges = async (): Promise<void> => {
    await setScoreboardSettings(currentSettings);
    props.onClose();
  };

  return (
    <InnerPopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Scoreboard Settings</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          <SettingsContainer>
            <ControlSpacer style={{ flex: 1 }}>
              <ControlLabel>Test Type</ControlLabel>
              <TestTypeSelect
                onChange={handleTestTypeChange}
                value={currentSettings.testType}
                disabled={false}
              />
            </ControlSpacer>
            <ControlSpacer style={{ flex: 1 }}>
              <ControlLabel>Tags</ControlLabel>
              <TagSelect
                onChange={handleTagsChange}
                value={currentSettings.tags}
                disabled={false}
              />
            </ControlSpacer>
            <ControlSpacer style={{ flex: 1 }}>
              <ControlLabel>Metric</ControlLabel>
              <SingleMetricSelect
                onChange={handleMetricChange}
                testTypeId={
                  !isEmpty(currentSettings.testType)
                    ? currentSettings.testType.value
                    : ''
                }
                disabled={isEmpty(currentSettings.testType)}
                value={
                  isEmpty(
                    currentSettings.metrics[currentSettings.testType.value]
                  )
                    ? null
                    : currentSettings.metrics[currentSettings.testType.value]
                }
              />
            </ControlSpacer>
            <ControlSpacer>
              <SlidingDateRangeSelect
                dateRangeType={currentSettings.dateRangeType}
                dateRange={currentSettings.dateRange}
                disallowLastSession
                onChange={handleDateChange}
              />
            </ControlSpacer>
          </SettingsContainer>
        </CardBody>
        <CardFooter>
          <CardFooterLeft>
            <Button onClick={props.onClose} icon='close' title='Close' style={{ whiteSpace: 'nowrap' }} />
          </CardFooterLeft>
          <CardFooterRight>
            <SaveButton
              onClick={applyChanges}
              disabled={
                !Boolean(
                  currentSettings?.metrics?.[currentSettings?.testType?.value]
                )
              }
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
interface IProps {
  settings: IScoreboardSettings;
  disabled: boolean;
}
export const ScoreboardSettings = withRouter(
  (props: IProps & RouteComponentProps): React.ReactElement => {
    const onOpen = (): void => {
      openInnerPopup(
        <Popup {...props} onClose={closeInnerPopup(props.history)} />,
        props.history,
        'edit-settings'
      );
    };

    return (
      <SettingsButton onClick={onOpen} />
    );
  }
);

const SettingsContainer = styled.div`
  padding: 10px;
`;
